import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  apiRequest,
  errorFormatHandler,
  isDevelop,
} from "../../helpers/apiHelper";
import { AddAllDaysItemScheduleInterface } from "../../pages/Expedition/components/ConfigWorkingDays";

import { DropdownValue } from "../../components/misc/Dropdown";
import {
  closeGlobalToast,
  setGlobalToast,
} from "../reducer/globalComponents.reducer";

const apiEnv = isDevelop
  ? process.env.REACT_APP_TEST_API_URL
  : process.env.REACT_APP_PUBLIC_API_URL;

const handleSetGlobalToast = (
  dispatch: any,
  message?: string,
  severity = "error",
) => {
  if (message) {
    dispatch(setGlobalToast({ message, severity }));
  } else {
    dispatch(closeGlobalToast());
  }
};

export const getScheduleWorkingPoints = createAsyncThunk(
  "/expedition/scheduleWorkingPoints",
  async (
    params: {
      page?: number;
      perPage?: number;
      deliveryPoint: string;
      route: string;
    },
    { rejectWithValue, dispatch },
  ) => {
    const { page, perPage, deliveryPoint } = params;

    try {
      const { data } = await apiRequest(
        `/nomPuncteLucru?page=${page}&perPage=${perPage}&searchPctLucru=${deliveryPoint}&searchRuta=${params.route}`,
        "GET",
      );
      return data;
    } catch (err: any) {
      console.warn("/expedition/scheduleWorkingPoints", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const getScheduleWorkingPointsDetails = createAsyncThunk(
  "/expedition/getScheduleWorkingPointsDetails",
  async (
    params: {
      id: number;
    },
    { rejectWithValue, dispatch },
  ) => {
    const { id: idRuta } = params;
    try {
      const { data } = await apiRequest(`/selectPctLucru/${idRuta}`, "GET");
      return data;
    } catch (err: any) {
      console.warn("/expedition/getScheduleWorkingPointsDetails", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const postCreateOrUpdateWorkingPoint = createAsyncThunk(
  "/expedition/postCreateOrUpdateWorkingPoint",
  async (
    params: {
      id: number;
      adresa: string;
      denumire: string;
      localitate: string;
      judet: string;
      cod_postal: string;
      pers_contact: string;
      tel_contact: string;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest(
        "/createOrUpdatePctLucru",
        "POST",
        params,
      );
      return data;
    } catch (err: any) {
      console.warn("/expedition/postCreateOrUpdateWorkingPoint", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const getWorkingRoutes = createAsyncThunk(
  "/expedition/getWorkingRoutes",
  async (
    params: {
      page?: number;
      perPage?: number;
    },
    { rejectWithValue, dispatch },
  ) => {
    const { page, perPage } = params;

    try {
      const { data } = await apiRequest(
        `/nomRute?page=${page}&perPage=${perPage}`,
        "GET",
      );
      return data;
    } catch (err: any) {
      console.warn("/expedition/getWorkingRoutes", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const getWorkingRoutesDetails = createAsyncThunk(
  "/expedition/getWorkingRoutesDetails",
  async (
    params: {
      id: number;
    },
    { rejectWithValue, dispatch },
  ) => {
    const { id: idRuta } = params;
    try {
      const { data } = await apiRequest(`/selectRuta/${idRuta}`, "GET");
      return data;
    } catch (err: any) {
      console.warn("/expedition/getWorkingRoutesDetails", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const postScheduleRoutes = createAsyncThunk(
  "/expedition/postScheduleRoutes",
  async (
    params: {
      activeWorkingDays: AddAllDaysItemScheduleInterface[];
      internode: boolean;
      id: number;
      selectedNode: DropdownValue;
      name: string;
      code: string;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest("/createOrUpdateRuta", "POST", params);
      return data;
    } catch (err: any) {
      console.warn("/expedition/postScheduleRoutes", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const fetchGetClientsNodes = createAsyncThunk(
  "/expedition/fetchGetClientsNodes",
  async (
    params: {
      page?: number;
      perPage?: number;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest("/displayNoduri", "GET", params);
      return data;
    } catch (err: any) {
      console.warn("/expedition/fetchGetClientsNodes", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const postEditNodeDetails = createAsyncThunk(
  "/expedition/postEditNodeDetails",
  async (
    {
      callBack,
      params,
    }: {
      callBack?: () => void;
      params: {
        nodId: number;
        auto: string;
        sofer: string;
      };
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest("/editNod", "POST", params);
      callBack && callBack();
      return data;
    } catch (err: any) {
      console.warn("/expedition/postEditNodeDetails", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const fetchUndeliveredAwb = createAsyncThunk(
  "/fetchUndeliveredAwb",
  async (
    params: {
      page: number;
      perPage: number;
      pctlivrare?: string;
      nodDestinatie?: string;
      doc_trans?: string;
      status?: string;
    },
    { rejectWithValue, getState },
  ) => {
    const state: any = getState();

    const {
      user: {
        data: { id_client },
      },
    } = state;

    try {
      const { data } = await apiRequest("/displayUndeliveredAwbs", "GET", {
        ...params,
        id_client,
      });
      return data;
    } catch (err: any) {
      console.warn("/fetchUndeliveredAwb", err);
      return rejectWithValue(errorFormatHandler(err));
    }
  },
);

export const postAssociateAwbToBin = createAsyncThunk(
  "/expedition/postAssociateAwbToBin",
  async (
    {
      params,
    }: {
      callBack?: () => void;
      params: {
        id_doctrans: number | string;
      };
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest("/associateAwbToBin", "POST", params);

      return data;
    } catch (err: any) {
      console.warn("/expedition/postAssociateAwbToBin", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const fetchPrintBinA4 = createAsyncThunk(
  "/expedition/fetchPrintBinA4",
  async (
    {
      idBin,
    }: {
      idBin?: number | string;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      window.open(`${apiEnv}/printSnBinA4/${idBin}`, "_blank")?.focus();
    } catch (err: any) {
      console.warn("/expedition/fetchPrintBinA4", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const fetchPrintBinZebra = createAsyncThunk(
  "/expedition/fetchPrintBinZebra",
  async (
    {
      idBin,
    }: {
      idBin?: number | string;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      window.open(`${apiEnv}/printSnBinZebra/${idBin}`, "_blank")?.focus();
    } catch (err: any) {
      console.warn("/expedition/fetchPrintBinZebra", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const fetchPrintAwbA4 = createAsyncThunk(
  "/expedition/fetchPrintAwbA4",
  async (
    {
      idDocTrans,
    }: {
      idDocTrans?: number | string;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      window.open(`${apiEnv}/printAwbA4/${idDocTrans}`, "_blank")?.focus();
    } catch (err: any) {
      console.warn("/expedition/fetchPrintAwbA4", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const fetchPrintAwbZebra = createAsyncThunk(
  "/expedition/fetchPrintAwbZebra",
  async (
    {
      idDocTrans,
    }: {
      idDocTrans?: number | string;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      window.open(`${apiEnv}/printAwbZebra/${idDocTrans}`, "_blank")?.focus();
    } catch (err: any) {
      console.warn("/expedition/fetchPrintAwbZebra", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const getAwbStatuses = createAsyncThunk(
  "/expedition/getAwbStatuses",
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await apiRequest("/getDocTransStatus", "GET", params);
      return data;
    } catch (err: any) {
      console.warn("/expedition/getAwbStatuses", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const fetchUndeliveredBins = createAsyncThunk(
  "/expedition/fetchUndeliveredBins",
  async (
    params: {
      page: number;
      perPage: number;
      sn_bin?: string;
      nodPreluare?: string;
      nodDestinatie?: string;
      status?: string;
    },
    { rejectWithValue, getState },
  ) => {
    const state: any = getState();

    const {
      user: {
        data: { id_client },
      },
    } = state;

    try {
      const { data } = await apiRequest("/displayUndeliveredBins", "GET", {
        ...params,
        id_client,
      });
      return data;
    } catch (err: any) {
      console.warn("/expedition/fetchUndeliveredBins", err);
      return rejectWithValue(errorFormatHandler(err));
    }
  },
);

export const getBinStatuses = createAsyncThunk(
  "/expedition/getBinStatuses",
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await apiRequest("/getBinStatus", "GET", params);
      return data;
    } catch (err: any) {
      console.warn("/expedition/getBinStatuses", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const fetchChangeWorkingPoint = createAsyncThunk(
  "/expedition/fetchChangeWorkingPoint",
  async (
    params: {
      id_doctrans: number;
      idPctLucru: number;
      id_client: string | number | null;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest(`/changeWorkingPoint`, "POST", params);
      return data;
    } catch (err: any) {
      console.warn("/expedition/fetchChangeWorkingPoint", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const postChangePickingDateUndeliveredAwb = createAsyncThunk(
  "/expedition/postChangePickingDateUndeliveredAwb",
  async (
    params: {
      id_doctrans: number;
      data_ridicare: string;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest(`/updatePickingDate`, "POST", params);
      return data;
    } catch (err: any) {
      console.warn("/expedition/postChangePickingDateUndeliveredAwb", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const postCancelAwb = createAsyncThunk(
  "/expedition/postCancelAwb",
  async (
    params: {
      id_doctrans: number;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest("/anuleazaAwb", "POST", params);
      handleSetGlobalToast(dispatch, data.message, "error");

      return data;
    } catch (err: any) {
      console.warn("/expedition/postCancelAwb", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const postDeleteBin = createAsyncThunk(
  "/expedition/postDeleteBin",
  async (
    params: {
      id_bin?: string | number;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest("/deleteBin", "GET", params);

      return data;
    } catch (err: any) {
      console.warn("/expedition/postDeleteBin", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const postMarkIndividualPackages = createAsyncThunk(
  "/expedition/postMarkIndividualPackages",
  async (
    params: {
      id_doctrans?: string | number;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const { data } = await apiRequest(
        "/markIndividualPackages",
        "POST",
        params,
      );

      return data;
    } catch (err: any) {
      console.warn("/expedition/postMarkIndividualPackages", err);
      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);

export const fetchDeliveredAwbHistory = createAsyncThunk(
  "expedition/fetchDeliveredAwbHistory",
  async (
    params: {
      page: number;
      perPage: number;
      pctlivrare?: string;
      nodDestinatie?: string;
      doc_trans?: string;
      status?: string;
      coldFilter?: number;
      facturi?: string;
    },
    { rejectWithValue, getState },
  ) => {
    const state: any = getState();

    const {
      user: {
        data: { id_client },
      },
    } = state;

    try {
      const { data } = await apiRequest("/displayDeliveredAwbs", "GET", {
        ...params,
        id_client,
      });

      return data;
    } catch (err: any) {
      console.warn("/fetchDeliveredAwbHistory", err);

      return rejectWithValue(errorFormatHandler(err));
    }
  },
);

export const fetchDeliverySheets = createAsyncThunk(
  "/expedition/fetchDeliverySheets",
  async (
    params: {
      page?: number;
      perPage?: number;
      nod?: string;
      ruta?: string;
      data_crearii?: string;
    },
    { rejectWithValue, getState },
  ) => {
    const state: any = getState();

    const {
      user: {
        data: { id_client },
      },
    } = state;

    try {
      const { data } = await apiRequest("/displayDeliverySheets", "GET", {
        ...params,
        id_client,
      });

      return data;
    } catch (err: any) {
      console.warn("/fetchDeliverySheets", err);

      return rejectWithValue(errorFormatHandler(err));
    }
  },
);
export const generateDeliverySheets = createAsyncThunk(
  "/expedition/generateDeliverySheets",
  async (_, { rejectWithValue, getState, dispatch }) => {
    const state: any = getState();

    const {
      user: {
        data: { id_client },
      },
    } = state;

    try {
      return await apiRequest(
        "/generareBorderouri",
        "POST",
        {
          id_client,
        },
        undefined,
        {
          responseType: "blob",
        },
      );
    } catch (err: any) {
      const errorText = await err.response.data.text();
      err.response.data = JSON.parse(errorText);

      console.warn("/generateDeliverySheets", err);

      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);
export const fetchReprintDeliverySheet = createAsyncThunk(
  "/expedition/fetchReprintDeliverySheet",
  async (
    {
      idBorderou,
    }: {
      idBorderou?: number | string;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      return await apiRequest(
        `/reprintBorderou/${idBorderou}`,
        "GET",
        undefined,
        undefined,
        {
          responseType: "blob",
        },
      );
    } catch (err: any) {
      const errorText = await err.response.data.text();
      err.response.data = JSON.parse(errorText);

      console.warn("/expedition/fetchReprintDeliverySheet", err);

      return rejectWithValue(errorFormatHandler(err, dispatch));
    }
  },
);
